import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { configureBrandDriveParent } from '../franchise/api';

// OrganicAssets_AutoFolderCreation_Spec §6.4 / §9.1. Brand Drive Rollout
// Parent Folder control. The AM pastes the folder's share link (or bare ID)
// and clicks Validate & save — the server checks the folder exists and that
// the Kyck service account has Editor on it, then persists. The resolved
// folder name confirms the binding.
//
// Edit-mode only: validation hits the dedicated endpoint keyed by accountId,
// which only exists once the brand (+ its FranchiseProfile) is saved. In
// create mode the AccountForm renders a plain input that saves the raw value
// with the rest of the profile; the AM validates afterwards by editing.

type Status =
  | { kind: 'idle' }
  | { kind: 'validating' }
  | { kind: 'ok'; folderName: string | null }
  | { kind: 'error'; message: string };

export function BrandDriveSettings({
  accountId,
  value,
  onChange,
  disabled,
}: {
  accountId: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const busy = status.kind === 'validating';

  const validate = async () => {
    setStatus({ kind: 'validating' });
    try {
      const trimmed = value.trim();
      const result = await configureBrandDriveParent(accountId, trimmed === '' ? null : trimmed);
      // Sync the form slice to the parsed/stored folder id so a later generic
      // profile save writes the same canonical value.
      onChange(result.profile.driveRolloutParentFolderId ?? '');
      setStatus({ kind: 'ok', folderName: result.folderName });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Validation failed. Try again.',
      });
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">
        Drive Rollout Parent Folder
      </label>
      <div className="flex items-start gap-2">
        <input
          type="text"
          value={value}
          disabled={disabled || busy}
          placeholder="Paste the folder share link or ID…"
          onChange={(e) => {
            onChange(e.target.value);
            if (status.kind !== 'idle') setStatus({ kind: 'idle' });
          }}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
        />
        <button
          type="button"
          onClick={validate}
          disabled={disabled || busy}
          className="shrink-0 rounded-md bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {busy ? 'Validating…' : 'Validate & save'}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        All Rollout folder hierarchies are created under this folder. It must exist in Drive
        and be shared with the Kyck service account as <strong>Editor</strong> (see
        docs/Brand_Drive_Setup.md). Leave blank + Validate to clear.
      </p>
      {status.kind === 'ok' && (
        <p className="text-xs text-emerald-700">
          {status.folderName
            ? `✓ Bound to "${status.folderName}".`
            : '✓ Cleared — folder creation is disabled for this brand.'}
        </p>
      )}
      {status.kind === 'error' && (
        <p className="text-xs text-red-600">{status.message}</p>
      )}
    </div>
  );
}
